import { useCallback, useEffect, useRef } from "react"
import type { ReactNode } from "react"
import { ArrowBack } from "@mui/icons-material"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { SkillNavButton } from "./skill-nav-button"

const PAGE_SX = { position: "absolute", inset: 0, zIndex: 1, display: "flex", flexDirection: "column", bgcolor: "var(--app-bg-paper)" } satisfies SxProps<Theme>
const HEADER_SX = { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, px: 2, py: 1, borderBottom: 1, borderColor: "divider" } satisfies SxProps<Theme>
const TITLE_SX = { fontWeight: 700 } satisfies SxProps<Theme>
// The scroll container's padding. Exported because a sticky child has to cancel it on both axes: `top` is
// resolved against this element's PADDING box, so a sticky bar at top:0 pins one padding-step below the
// header no matter what its margins say.
export const SKILLS_BODY_PADDING = 2
const BODY_SX = { flex: 1, minHeight: 0, overflowY: "auto", p: SKILLS_BODY_PADDING } satisfies SxProps<Theme>

interface KellySkillsPageProps {
  children?: ReactNode
  /** Return to the list. Absent on the list itself, which has nothing to go back to. */
  onBack?: () => void
  /** Changes when the body's content does, so the new content starts at the top. */
  scrollResetKey: string
}

export function KellySkillsPage({ children, onBack, scrollResetKey }: KellySkillsPageProps) {
  const pageRef = useRef<HTMLElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  // The body is the one scroll container, and swapping the list for a detail does not touch it -- so a
  // detail opened from the bottom of a long grid starts scrolled past its own title and back control.
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0 }, [scrollResetKey])

  // Every opener unmounts as it fires -- the dock flyout closes over its own row -- so focus lands on
  // <body>, outside the <aside> the dock's Escape ladder is bound to, and the pane loses its keyboard
  // exit. The list has no header control to take focus, so the section itself does.
  const takeFocus = useCallback(() => { (backRef.current ?? pageRef.current)?.focus() }, [])

  useEffect(() => {
    const opener = document.activeElement
    takeFocus()
    return () => {
      const active = document.activeElement
      const focusIsLost = active === null || active === document.body
      const openerStands = opener instanceof HTMLElement && opener !== document.body && opener.isConnected
      if (focusIsLost && openerStands) opener.focus()
    }
  }, [takeFocus])

  useEffect(() => {
    const active = document.activeElement
    if (active === null || active === document.body) takeFocus()
  }, [scrollResetKey, takeFocus])

  return (
    <Box component="section" ref={pageRef} tabIndex={-1} aria-label="Skills" sx={PAGE_SX} data-testid="kelly-skills-page">
      <Box sx={HEADER_SX}>
        {onBack && (
          <SkillNavButton
            ref={backRef}
            startIcon={<ArrowBack fontSize="small" />}
            onClick={onBack}
            data-testid="kelly-skills-back-to-list"
          >
            All skills
          </SkillNavButton>
        )}
        <Typography component="h2" variant="h6" sx={TITLE_SX}>Skills</Typography>
      </Box>
      <Box ref={bodyRef} sx={BODY_SX} data-testid="kelly-skills-page-body">{children}</Box>
    </Box>
  )
}
